import React, { useEffect } from 'react';
import styled from 'styled-components';
import { cssColorValue, semanticColors, spacer16, spacer24, spacer32, Type } from '@spotify-internal/encore-web';

import Metatags from 'components/Metatags/Metatags';
import PageContainer from 'components/PageContainer';
import Breadcrumbs from 'components/Breadcrumbs';
import BlockSerializer from 'lib/sanity/ui/BlockSerializer';
import WarningBanner from 'components/WarningBanner/WarningBanner';
import analytics from 'utils/client/analytics/analytics';
import { useUbiArticlePage } from 'utils/ubi/pages/useUbiArticle';
import { BlockSerializerUbiContext } from 'lib/sanity/ui/BlockSerializerUbiProvider';
import { TreatedArticleData, TreatmentState } from 'utils/remote-config/content/types';
import { FeedbackStickyWidget, FeedbackCurrentWidget, FeedbackPopUp } from 'experiments/feedback-position';

import RelatedArticles from './RelatedArticles';

export interface ArticleProps {
  article: TreatedArticleData;
  articleSlug: string;
  isPlain: boolean;
}

const Title = styled(Type)`
  word-break: break-word;
  hyphens: auto;
`;

const Card = styled.div`
  margin-top: ${spacer32};
  padding: ${spacer24} ${spacer16};
  border-radius: 8px;
  background: ${cssColorValue(semanticColors.backgroundElevatedHighlight)};
`;

function getTreatmentIdentifier(article: TreatedArticleData, articleSlug: string): string {
  switch (article.treatmentState) {
    case TreatmentState.Control:
      return `${articleSlug}_control`;
    case TreatmentState.Treatment:
      return `${articleSlug}_treatment_${article.treatmentKey}`;
    default:
      return articleSlug;
  }
}

const Article = ({ article, isPlain, articleSlug }: ArticleProps) => {
  const articleIdentifier = getTreatmentIdentifier(article, articleSlug);
  const ubi = useUbiArticlePage(articleIdentifier);

  useEffect(() => {
    if (articleSlug !== articleIdentifier) {
      analytics.trackExperiment(articleIdentifier);
    }
    analytics.trackPageView(`/article/${articleSlug}/`);
    ubi.logArticleImpression();
  }, [articleIdentifier, ubi, articleSlug]);

  const { content, title, relatedArticles, category, banner } = article;

  const parentCategory = category?.parentCategory;

  return (
    <>
      <Metatags title={title as string} urlPath={`article/${articleSlug}/`} />
      {!isPlain && <WarningBanner banner={banner} />}
      <PageContainer>
        {!isPlain && parentCategory && <Breadcrumbs parentCategory={parentCategory} article={article} ubi={ubi} />}

        {content && (
          <Card>
            <Title forwardedAs="h1" variant="altoBrio" paddingBottom={spacer16}>
              {title}
            </Title>
            <BlockSerializerUbiContext.Provider value={ubi}>
              <BlockSerializer value={content} />
            </BlockSerializerUbiContext.Provider>
          </Card>
        )}
        {!isPlain && <FeedbackPopUp articleIdentifier={articleIdentifier} ubi={ubi} />}
        {!isPlain && relatedArticles.length > 0 && (
          <Card>
            <RelatedArticles relatedArticles={relatedArticles} ubi={ubi} />
          </Card>
        )}

        {!isPlain && <FeedbackStickyWidget articleIdentifier={articleIdentifier} ubi={ubi} />}
        {!isPlain && <FeedbackCurrentWidget articleIdentifier={articleIdentifier} ubi={ubi} />}
      </PageContainer>
    </>
  );
};

export default Article;
